import { Box, Icon, Table, Text } from '@factorialco/deprecated-design-system'

import createMemo from 'modules/core/hooks/createMemo'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { dateBackendDateTimeToUI } from 'modules/core/lib/dates'
import { matchText } from 'modules/core/lib/stringUtils'

import type { Employees } from 'modules/employees/store/collections/employees'
import EmployeeLabelWithFallback from 'modules/surveys/components/Participants/common/EmployeeLabelWithFallback'
import Location from 'modules/surveys/components/Participants/common/Location'
import { SURVEY_MINIMUM_ANSWERS_TO_SHOW_ANONYMOUS } from 'modules/surveys/lib/constants'
import type SurveyForm from 'modules/surveys/store/models/Form'
import type QuestionGroup from 'modules/surveys/store/models/QuestionGroup'
import type Survey from 'modules/surveys/store/models/v2/Survey'

const useFilter = createMemo(
  (questionGroups: QuestionGroup[], form: SurveyForm | Survey, search: string) =>
    questionGroups.filter((questionGroup) => {
      if (form.isAnonymous) return true

      const respondent = questionGroup.attributes.respondent
      return respondent?.full_name ? matchText(search, respondent.full_name) : false
    })
)

interface Props {
  form: SurveyForm | Survey
  questionGroups: QuestionGroup[]
  search: string
  employees: Employees
}

export default function ResponsesBody({ form, search, questionGroups, employees }: Props) {
  const { navigation } = useNavigation()
  const filteredQuestionGroups = useFilter(questionGroups, form, search)
  const isCurrentUserIncluded = questionGroups.some(
    (questionGroup) => questionGroup.attributes.respondent
  )
  const hasMoreThanMinimumRespondents = isCurrentUserIncluded
    ? questionGroups.length > SURVEY_MINIMUM_ANSWERS_TO_SHOW_ANONYMOUS
    : questionGroups.length >= SURVEY_MINIMUM_ANSWERS_TO_SHOW_ANONYMOUS
  return (
    <Table.Body>
      {filteredQuestionGroups.map((questionGroup) => {
        const respondent = questionGroup.attributes.respondent
        const { id, answered_at } = questionGroup.attributes
        const canSeeResponseShowPage =
          !form.isAnonymous || hasMoreThanMinimumRespondents || !!respondent
        const route = canSeeResponseShowPage
          ? navigation.engagement.surveys.id.responses.questionGroupId.fullPath({
              id: form.attributes.id,
              question_group_id: id,
            })
          : ''

        return (
          <Table.Row.Router key={id} to={route}>
            <Table.Cell colSpan={2}>
              <EmployeeLabelWithFallback respondent={respondent} isAnonymous={form.isAnonymous} />
            </Table.Cell>
            <Table.Cell>
              {form.isAnonymous || !respondent ? (
                '-'
              ) : (
                <Location respondentId={respondent.id} employees={employees} />
              )}
            </Table.Cell>
            <Table.Cell>
              <Text.Normal>{dateBackendDateTimeToUI(answered_at)}</Text.Normal>
            </Table.Cell>
            <Table.Cell>
              {canSeeResponseShowPage && (
                <Box alignItems="flexEnd">
                  <Icon.Medium icon="arrow-right" />
                </Box>
              )}
            </Table.Cell>
          </Table.Row.Router>
        )
      })}
    </Table.Body>
  )
}
